import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Save, Shirt, FileDown, Shield, Plus, Trash2 } from "lucide-react";
import { exportClothingAndSafetyToPDF } from "@/lib/pdfExport";
import { SignatureCanvas } from "@/components/SignatureCanvas";

interface EmployeeClothingFormProps {
  employeeId: string;
  employeeName?: string;
  readOnly?: boolean;
}

interface CustomItem {
  id: string;
  name: string;
  size: string;
}

interface ClothingData {
  pants_size: string;
  tshirt_size: string;
  sweatshirt_size: string;
  fleece_jacket_size: string;
  winter_jacket_size: string;
  work_shoes_size: string;
  helmet_size: string;
  safety_glasses: string;
  gloves_size: string;
  ear_protection: string;
  custom_items: CustomItem[];
  signature_url: string | null;
  signed_at: string | null;
  signed_by: string | null;
}

const CLOTHING_SIZES = ["S", "M", "L", "XL", "2XL", "3XL"];
const SHOE_SIZES = ["39", "40", "41", "42", "43", "44", "45", "46", "47"];
const HELMET_SIZES = ["52-56", "56-59", "59-62", "62-65"];
const GLOVE_SIZES = ["6", "7", "8", "9", "10", "11", "12"];
const SAFETY_GLASSES_OPTIONS = ["Klar", "Getönt", "Klar + Getönt", "Mit Sehstärke"];
const EAR_PROTECTION_OPTIONS = ["Ohrstöpsel", "Kapselgehörschutz", "Bügelgehörschutz", "Beides"];

export function EmployeeClothingForm({ employeeId, employeeName = "Mitarbeiter", readOnly = false }: EmployeeClothingFormProps) {
  const { user, profile, userRole } = useAuth();
  const isEmployee = userRole === 'employee';
  const isAdmin = userRole === 'admin' || userRole === 'hr_manager';
  // Mitarbeiter dürfen nur Arbeitskleidung ändern, nicht PSA oder eigene Einträge
  const isClothingReadOnly = readOnly;
  const isSafetyReadOnly = readOnly || isEmployee;
  const isCustomItemsReadOnly = readOnly || isEmployee;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [signedByName, setSignedByName] = useState<string | null>(null);
  const [formData, setFormData] = useState<ClothingData>({
    pants_size: "",
    tshirt_size: "",
    sweatshirt_size: "",
    fleece_jacket_size: "",
    winter_jacket_size: "",
    work_shoes_size: "",
    helmet_size: "",
    safety_glasses: "",
    gloves_size: "",
    ear_protection: "",
    custom_items: [],
    signature_url: null,
    signed_at: null,
    signed_by: null,
  });
  const [customInputs, setCustomInputs] = useState<Record<string, boolean>>({});
  const [newCustomItem, setNewCustomItem] = useState({ name: "", size: "" });

  useEffect(() => {
    if (user && employeeId) {
      loadClothingData();
    }
  }, [user, employeeId]);

  const loadClothingData = async () => {
    try {
      const { data, error } = await supabase
        .from("employee_clothing")
        .select("*")
        .eq("employee_id", employeeId)
        .maybeSingle();

      if (error) throw error;

      if (data) {
        const rawCustomItems = Array.isArray(data.custom_items) ? data.custom_items : [];
        const customItems: CustomItem[] = rawCustomItems.map((item: unknown) => {
          const obj = item as Record<string, unknown>;
          return {
            id: String(obj.id || crypto.randomUUID()),
            name: String(obj.name || ''),
            size: String(obj.size || ''),
          };
        });
        
        const newFormData: ClothingData = {
          pants_size: data.pants_size || "",
          tshirt_size: data.tshirt_size || "",
          sweatshirt_size: data.sweatshirt_size || "",
          fleece_jacket_size: data.fleece_jacket_size || "",
          winter_jacket_size: data.winter_jacket_size || "",
          work_shoes_size: data.work_shoes_size || "",
          helmet_size: data.helmet_size || "",
          safety_glasses: data.safety_glasses || "",
          gloves_size: data.gloves_size || "",
          ear_protection: data.ear_protection || "",
          custom_items: customItems,
          signature_url: data.signature_url || null,
          signed_at: data.signed_at || null,
          signed_by: data.signed_by || null,
        };
        setFormData(newFormData);

        // Determine which fields have custom values
        const customFields: Record<string, boolean> = {};
        if (newFormData.pants_size && !CLOTHING_SIZES.includes(newFormData.pants_size)) customFields.pants_size = true;
        if (newFormData.tshirt_size && !CLOTHING_SIZES.includes(newFormData.tshirt_size)) customFields.tshirt_size = true;
        if (newFormData.sweatshirt_size && !CLOTHING_SIZES.includes(newFormData.sweatshirt_size)) customFields.sweatshirt_size = true;
        if (newFormData.fleece_jacket_size && !CLOTHING_SIZES.includes(newFormData.fleece_jacket_size)) customFields.fleece_jacket_size = true;
        if (newFormData.winter_jacket_size && !CLOTHING_SIZES.includes(newFormData.winter_jacket_size)) customFields.winter_jacket_size = true;
        if (newFormData.work_shoes_size && !SHOE_SIZES.includes(newFormData.work_shoes_size)) customFields.work_shoes_size = true;
        if (newFormData.helmet_size && !HELMET_SIZES.includes(newFormData.helmet_size)) customFields.helmet_size = true;
        if (newFormData.gloves_size && !GLOVE_SIZES.includes(newFormData.gloves_size)) customFields.gloves_size = true;
        if (newFormData.safety_glasses && !SAFETY_GLASSES_OPTIONS.includes(newFormData.safety_glasses)) customFields.safety_glasses = true;
        if (newFormData.ear_protection && !EAR_PROTECTION_OPTIONS.includes(newFormData.ear_protection)) customFields.ear_protection = true;
        setCustomInputs(customFields);

        // Load signed by name
        if (data.signed_by) {
          const { data: profileData } = await supabase
            .from("profiles")
            .select("full_name")
            .eq("id", data.signed_by)
            .maybeSingle();
          if (profileData) {
            setSignedByName(profileData.full_name);
          }
        }
      }
    } catch (error) {
      console.error("Error loading clothing data:", error);
      toast.error("Fehler beim Laden der Arbeitskleidung");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data: existing } = await supabase
        .from("employee_clothing")
        .select("*")
        .eq("employee_id", employeeId)
        .maybeSingle();

      // Mitarbeiter dürfen nur Arbeitskleidung speichern, nicht PSA oder eigene Einträge
      const saveData = isEmployee ? {
        pants_size: formData.pants_size || null,
        tshirt_size: formData.tshirt_size || null,
        sweatshirt_size: formData.sweatshirt_size || null,
        fleece_jacket_size: formData.fleece_jacket_size || null,
        winter_jacket_size: formData.winter_jacket_size || null,
        work_shoes_size: formData.work_shoes_size || null,
        // PSA und custom_items bleiben unverändert für Mitarbeiter
        ...(existing ? {
          helmet_size: existing.helmet_size,
          safety_glasses: existing.safety_glasses,
          gloves_size: existing.gloves_size,
          ear_protection: existing.ear_protection,
          custom_items: existing.custom_items,
        } : {
          helmet_size: null,
          safety_glasses: null,
          gloves_size: null,
          ear_protection: null,
          custom_items: [],
        }),
      } : {
        pants_size: formData.pants_size || null,
        tshirt_size: formData.tshirt_size || null,
        sweatshirt_size: formData.sweatshirt_size || null,
        fleece_jacket_size: formData.fleece_jacket_size || null,
        winter_jacket_size: formData.winter_jacket_size || null,
        work_shoes_size: formData.work_shoes_size || null,
        helmet_size: formData.helmet_size || null,
        safety_glasses: formData.safety_glasses || null,
        gloves_size: formData.gloves_size || null,
        ear_protection: formData.ear_protection || null,
        custom_items: JSON.parse(JSON.stringify(formData.custom_items)),
      };

      if (existing) {
        const { error } = await supabase
          .from("employee_clothing")
          .update(saveData)
          .eq("employee_id", employeeId);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("employee_clothing")
          .insert([{ employee_id: employeeId, ...saveData }]);

        if (error) throw error;
      }

      toast.success(isEmployee ? "Arbeitskleidung gespeichert" : "Arbeitskleidung & Sicherheitsausrüstung gespeichert");
    } catch (error) {
      console.error("Error saving clothing data:", error);
      toast.error("Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  const handleSignature = async (signatureDataUrl: string) => {
    try {
      const base64Data = signatureDataUrl.split(',')[1];
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'image/png' });

      const fileName = `clothing_signature_${employeeId}_${Date.now()}.png`;
      const filePath = `${employeeId}/${fileName}`;

      // Use employee-profiles bucket which is public
      const { error: uploadError } = await supabase.storage
        .from('employee-profiles')
        .upload(filePath, blob, { contentType: 'image/png', upsert: true });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('employee-profiles')
        .getPublicUrl(filePath);

      const { data: existing } = await supabase
        .from("employee_clothing")
        .select("id")
        .eq("employee_id", employeeId)
        .maybeSingle();

      const signatureData = {
        signature_url: publicUrl,
        signed_at: new Date().toISOString(),
        signed_by: employeeId, // Use employee ID, not logged-in user
      };

      if (existing) {
        const { error } = await supabase
          .from("employee_clothing")
          .update(signatureData)
          .eq("employee_id", employeeId);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("employee_clothing")
          .insert([{ employee_id: employeeId, ...signatureData }]);
        if (error) throw error;
      }

      setFormData(prev => ({
        ...prev,
        signature_url: publicUrl,
        signed_at: signatureData.signed_at,
        signed_by: employeeId,
      }));
      setSignedByName(employeeName); // Use employee name, not logged-in user
      toast.success("Unterschrift gespeichert");
    } catch (error) {
      console.error("Error saving signature:", error);
      toast.error("Fehler beim Speichern der Unterschrift");
    }
  };

  const handleDeleteSignature = async () => {
    try {
      const { error } = await supabase
        .from("employee_clothing")
        .update({
          signature_url: null,
          signed_at: null,
          signed_by: null,
        })
        .eq("employee_id", employeeId);

      if (error) throw error;

      setFormData(prev => ({
        ...prev,
        signature_url: null,
        signed_at: null,
        signed_by: null,
      }));
      setSignedByName(null);
      toast.success("Unterschrift gelöscht");
    } catch (error) {
      console.error("Error deleting signature:", error);
      toast.error("Fehler beim Löschen der Unterschrift");
    }
  };

  const toggleCustomInput = (key: string) => {
    setCustomInputs(prev => ({ ...prev, [key]: !prev[key] }));
    if (!customInputs[key]) {
      setFormData(prev => ({ ...prev, [key]: "" }));
    }
  };

  const addCustomItem = () => {
    if (!newCustomItem.name.trim()) {
      toast.error("Bitte geben Sie einen Namen ein");
      return;
    }
    const newItem: CustomItem = {
      id: crypto.randomUUID(),
      name: newCustomItem.name.trim(),
      size: newCustomItem.size.trim(),
    };
    setFormData(prev => ({
      ...prev,
      custom_items: [...prev.custom_items, newItem],
    }));
    setNewCustomItem({ name: "", size: "" });
  };

  const removeCustomItem = (id: string) => {
    setFormData(prev => ({
      ...prev,
      custom_items: prev.custom_items.filter(item => item.id !== id),
    }));
  };

  const handleExportPDF = async () => {
    await exportClothingAndSafetyToPDF(
      employeeName,
      formData,
      {
        signatureUrl: formData.signature_url || undefined,
        signedAt: formData.signed_at || undefined,
        signedByName: signedByName || undefined,
      }
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  const clothingItems = [
    { key: "pants_size", label: "Hosengröße", icon: "👖", sizes: CLOTHING_SIZES },
    { key: "tshirt_size", label: "T-Shirt Größe", icon: "👕", sizes: CLOTHING_SIZES },
    { key: "sweatshirt_size", label: "Sweatshirt Größe", icon: "🧥", sizes: CLOTHING_SIZES },
    { key: "fleece_jacket_size", label: "Fleecejacke Größe", icon: "🧤", sizes: CLOTHING_SIZES },
    { key: "winter_jacket_size", label: "Winterjacke Größe", icon: "🧥", sizes: CLOTHING_SIZES },
    { key: "work_shoes_size", label: "Arbeitsschuhe Größe", icon: "👟", sizes: SHOE_SIZES },
  ];

  const safetyItems = [
    { key: "helmet_size", label: "Helm Größe", icon: "⛑️", sizes: HELMET_SIZES },
    { key: "gloves_size", label: "Handschuhe Größe", icon: "🧤", sizes: GLOVE_SIZES },
    { key: "safety_glasses", label: "Schutzbrille", icon: "🥽", sizes: SAFETY_GLASSES_OPTIONS },
    { key: "ear_protection", label: "Gehörschutz", icon: "🎧", sizes: EAR_PROTECTION_OPTIONS },
  ];

  const renderSizeField = (item: { key: string; label: string; icon: string; sizes: string[] }, fieldReadOnly: boolean) => {
    const isCustom = customInputs[item.key];
    const currentValue = formData[item.key as keyof ClothingData] as string;

    return (
      <div key={item.key} className="space-y-2">
        <Label className="flex items-center gap-2">
          <span className="text-xl">{item.icon}</span>
          {item.label}
        </Label>
        
        {fieldReadOnly ? (
          <Input
            value={currentValue || "-"}
            readOnly
            disabled
            className="bg-muted"
          />
        ) : isCustom ? (
          <div className="flex gap-2">
            <Input
              value={currentValue}
              onChange={(e) => setFormData({ ...formData, [item.key]: e.target.value })}
              placeholder="Eigene Angabe..."
              className="flex-1"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={() => toggleCustomInput(item.key)}
              title="Zurück zur Auswahl"
            >
              Liste
            </Button>
          </div>
        ) : (
          <div className="flex gap-2">
            <Select
              value={currentValue}
              onValueChange={(value) => setFormData({ ...formData, [item.key]: value })}
            >
              <SelectTrigger className="flex-1">
                <SelectValue placeholder="Auswählen..." />
              </SelectTrigger>
              <SelectContent>
                {item.sizes.map((size) => (
                  <SelectItem key={size} value={size}>
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="sm"
              onClick={() => toggleCustomInput(item.key)}
              title="Individuelle Angabe eingeben"
            >
              Andere
            </Button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Arbeitskleidung */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Shirt className="h-6 w-6 text-primary" />
            </div>
            <div>
              <CardTitle>Arbeitskleidung</CardTitle>
              <CardDescription>Größen für Nachbestellungen</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {clothingItems.map((item) => renderSizeField(item, isClothingReadOnly))}
          </div>
        </CardContent>
      </Card>

      {/* Sicherheitsausrüstung */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-500/10 rounded-lg">
              <Shield className="h-6 w-6 text-orange-500" />
            </div>
            <div>
              <CardTitle>Sicherheitsausrüstung (PSA)</CardTitle>
              <CardDescription>Persönliche Schutzausrüstung</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isSafetyReadOnly && isEmployee && (
            <p className="text-sm text-muted-foreground mb-4">
              Sicherheitsausrüstung kann nur von Administratoren oder HR geändert werden.
            </p>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {safetyItems.map((item) => renderSizeField(item, isSafetyReadOnly))}
          </div>
        </CardContent>
      </Card>

      {/* Eigene Einträge */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-500/10 rounded-lg">
              <Plus className="h-6 w-6 text-green-500" />
            </div>
            <div>
              <CardTitle>Eigene Einträge</CardTitle>
              <CardDescription>Weitere Ausrüstung hinzufügen</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isCustomItemsReadOnly && isEmployee && (
            <p className="text-sm text-muted-foreground mb-4">
              Eigene Einträge können nur von Administratoren oder HR hinzugefügt werden.
            </p>
          )}
          <div className="space-y-4">
            {/* Existing custom items */}
            {formData.custom_items.length > 0 && (
              <div className="space-y-2">
                {formData.custom_items.map((item) => (
                  <div key={item.id} className="flex items-center gap-2 p-3 bg-muted rounded-lg">
                    <span className="flex-1 font-medium">{item.name}</span>
                    <span className="text-muted-foreground">{item.size || "-"}</span>
                    {!isCustomItemsReadOnly && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeCustomItem(item.id)}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Add new custom item - only for admins/HR */}
            {!isCustomItemsReadOnly && (
              <div className="flex gap-2">
                <Input
                  placeholder="Bezeichnung (z.B. Warnweste)"
                  value={newCustomItem.name}
                  onChange={(e) => setNewCustomItem(prev => ({ ...prev, name: e.target.value }))}
                  className="flex-1"
                />
                <Input
                  placeholder="Größe/Typ"
                  value={newCustomItem.size}
                  onChange={(e) => setNewCustomItem(prev => ({ ...prev, size: e.target.value }))}
                  className="w-32"
                />
                <Button onClick={addCustomItem} variant="outline">
                  <Plus className="h-4 w-4 mr-1" />
                  Hinzufügen
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Unterschrift */}
      <Card>
        <CardHeader>
          <CardTitle>Übergabe-Bestätigung</CardTitle>
          <CardDescription>
            Unterschrift für die Bestätigung des Empfangs der Arbeitskleidung und Sicherheitsausrüstung
          </CardDescription>
        </CardHeader>
        <CardContent>
          <SignatureCanvas
            onSave={handleSignature}
            onDelete={handleDeleteSignature}
            existingSignature={formData.signature_url || undefined}
            signedAt={formData.signed_at || undefined}
            signedByName={signedByName || undefined}
            isAdmin={isAdmin}
            disabled={!isAdmin && userRole === 'employee'}
            title="Empfangsbestätigung"
            description="Hiermit bestätige ich den Empfang der oben aufgeführten Arbeitskleidung und Sicherheitsausrüstung."
          />
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={handleExportPDF}>
          <FileDown className="mr-2 h-4 w-4" />
          PDF exportieren
        </Button>
        {/* Mitarbeiter können nur Arbeitskleidung speichern */}
        {(!isEmployee || !isClothingReadOnly) && (
          <Button onClick={handleSave} disabled={saving}>
            {saving ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            {isEmployee ? "Arbeitskleidung speichern" : "Speichern"}
          </Button>
        )}
      </div>
    </div>
  );
}
